import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Package, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PACKING_STATUS_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const CAN_EDIT = ["owner", "receiving"];
const FILTERS = [
  { key: "", label: "All" },
  { key: "packing", label: "Packing" },
  { key: "packed", label: "Packed" },
];

export default function Packing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [status, setStatus] = useState("");
  const [pickOpen, setPickOpen] = useState(false);
  const [eligible, setEligible] = useState([]);
  const [busy, setBusy] = useState(false);

  const canEdit = CAN_EDIT.includes(user.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/packing/jobs", { params: status ? { status } : {} });
      setJobs(data);
    } catch (e) {
      toast.error(apiError(e));
      setJobs([]);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = async () => {
    try {
      const [{ data: orders }, { data: allJobs }] = await Promise.all([api.get("/orders"), api.get("/packing/jobs")]);
      const taken = new Set(allJobs.map((j) => j.order_id));
      setEligible(
        orders.filter(
          (o) => o.status === "active" && !taken.has(o.id) && (o.items || []).some((i) => i.status === "active" && ((i.allocated_qty || 0) > 0 || (i.fulfillment_type === "READY" && i.ready_check_status === "CHECKED")))
        )
      );
      setPickOpen(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const createJob = async (orderId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/packing/jobs", { order_id: orderId });
      toast.success("Packing job created");
      setPickOpen(false);
      navigate(`/packing/jobs/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="packing-page">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Packing</h1>
          <p className="text-sm text-muted-foreground">Pack allocated units per customer order</p>
        </div>
        {canEdit && (
          <Button data-testid="new-packing-job-button" className="h-12 shrink-0 px-4" onClick={openPicker}>
            <Plus className="h-4 w-4" /> New Job
          </Button>
        )}
      </div>

      <div className="mb-4 flex gap-2" data-testid="packing-status-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`packing-filter-${f.key || "all"}`}
            onClick={() => setStatus(f.key)}
            className={`h-10 rounded-md border px-3 text-xs font-bold transition-colors ${
              status === f.key ? "border-primary bg-primary/5 text-primary" : "bg-card text-muted-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {jobs === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : jobs.length === 0 ? (
        <div data-testid="packing-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <Package className="mb-2 h-8 w-8 text-stone-300" />
          <p className="font-medium text-muted-foreground">No packing jobs yet</p>
          <p className="mt-1 text-xs text-muted-foreground">Jobs are created from orders with FIFO-allocated units</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="packing-jobs-list">
          {jobs.map((j) => {
            const meta = PACKING_STATUS_META[j.status] || PACKING_STATUS_META.packing;
            const req = j.items.reduce((s, i) => s + i.qty_required, 0);
            const packed = j.items.reduce((s, i) => s + i.qty_packed, 0);
            return (
              <button
                key={j.id}
                data-testid={`packing-job-${j.id}`}
                onClick={() => navigate(`/packing/jobs/${j.id}`)}
                className="w-full rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{j.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {j.batch_name} · #{j.id.slice(0, 8)} · {j.created_by_name}
                    </p>
                  </div>
                  <span className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-bold text-stone-700" data-testid={`packing-progress-${j.id}`}>
                    {packed}/{req} units packed
                  </span>
                  <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                    {j.items.length} item{j.items.length === 1 ? "" : "s"}
                  </span>
                  {j.evidence?.length > 0 && (
                    <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                      {j.evidence.length} photo{j.evidence.length === 1 ? "" : "s"}
                    </span>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground">{format(new Date(j.updated_at), "d MMM, HH:mm")}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Drawer open={pickOpen} onOpenChange={setPickOpen} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Select Order to Pack</DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[70vh] space-y-2 overflow-y-auto px-4 pb-8" data-testid="packing-order-picker">
            {eligible.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="packing-picker-empty">
                No orders ready to pack — orders need FIFO-allocated units first
              </p>
            ) : (
              eligible.map((o) => {
                const alloc = o.items.filter((i) => i.status === "active").reduce((s, i) => s + (i.allocated_qty || 0), 0);
                return (
                  <button
                    key={o.id}
                    data-testid={`packing-pick-order-${o.id}`}
                    disabled={busy}
                    onClick={() => createJob(o.id)}
                    className="w-full rounded-md border bg-card p-3.5 text-left transition-colors hover:border-primary"
                  >
                    <p className="text-sm font-bold">{o.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.batch_name} · #{o.id.slice(0, 8)} · <span className="font-bold text-emerald-700">{alloc} allocated units</span>
                    </p>
                  </button>
                );
              })
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
